import React, { useState, useRef, useEffect, useCallback } from "react";
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import NoImg from "../assets/images/no-image-icon-23485.png";

export interface Image {
  image_id: string;
  file_url: string;
  description?: string | null;
}

interface ImageGalleryProps {
  images: Image[];
  restaurantName: string;
  baseUrl?: string;
}

// ─── Shared helper ────────────────────────────────────────────────────────────
const getImageUrl = (fileUrl: string | undefined, baseUrl?: string) =>
  fileUrl ? `${baseUrl ?? ""}${fileUrl}` : NoImg;

// ─── Reusable thumbnail cell ──────────────────────────────────────────────────
interface ThumbProps {
  img: Image;
  index: number;
  restaurantName: string;
  baseUrl?: string;
  extraCount?: number; // show "+N" overlay when > 0
  className?: string;
  onClick: (index: number) => void;
}

const Thumb: React.FC<ThumbProps> = ({
  img,
  index,
  restaurantName,
  baseUrl,
  extraCount,
  className = "",
  onClick,
}) => (
  <div
    className={`relative overflow-hidden rounded-xl cursor-pointer group ${className}`}
    onClick={() => onClick(index)}
  >
    <img
      src={getImageUrl(img.file_url, baseUrl)}
      alt={img.description || restaurantName}
      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
      loading="lazy"
    />
    {/* Dark hover tint */}
    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors duration-300 rounded-xl" />
    {/* "+N more" overlay */}
    {extraCount !== undefined && extraCount > 0 && (
      <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl">
        <span className="text-white font-myYekanFaNumRegular text-xl font-semibold">
          +{extraCount}
        </span>
      </div>
    )}
  </div>
);

// ─── Desktop grid layouts ─────────────────────────────────────────────────────
// No flex-row-reverse: the Persian site's parent dir="rtl" makes flex rows
// start from the RIGHT already — first DOM child = rightmost on screen.
// flex-row-reverse would double-reverse and push the dominant image back to LEFT.
const GRID_WRAPPER =
  "hidden sm:flex gap-2 my-6 h-[300px] md:h-[380px] lg:h-[460px] xl:h-[500px]";

// ─── Main component ───────────────────────────────────────────────────────────
const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  restaurantName,
  baseUrl,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mobileIndex, setMobileIndex] = useState(0);

  // Swipe state
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const MIN_SWIPE = 50;

  if (!images || images.length === 0) return null;

  const count = images.length;

  const openModal = (index: number) => {
    setSelectedIndex(index);
    setIsModalOpen(true);
  };
  const closeModal = () => setIsModalOpen(false);

  const next = () => setSelectedIndex((p) => (p + 1) % count);
  const prev = () => setSelectedIndex((p) => (p - 1 + count) % count);

  const nextMobile = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setMobileIndex((p) => (p + 1) % count);
  };
  const prevMobile = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setMobileIndex((p) => (p - 1 + count) % count);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchEndX.current = 0;
    touchStartX.current = e.targetTouches[0].clientX;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };
  const onTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const d = touchStartX.current - touchEndX.current;
    if (d > MIN_SWIPE) nextMobile();
    else if (d < -MIN_SWIPE) prevMobile();
  };

  // ── 📱 Mobile carousel ──────────────────────────────────────────────────────
  const MobileGallery = () => (
    <div
      className="relative w-full sm:hidden rounded-xl overflow-hidden"
      style={{ aspectRatio: "4/3" }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <img
        src={getImageUrl(images[mobileIndex].file_url, baseUrl)}
        alt={images[mobileIndex].description || restaurantName}
        className="w-full h-full object-cover"
        loading="lazy"
        onClick={() => openModal(mobileIndex)}
      />

      {count > 1 && (
        <>
          {/* RTL: right side = start of reading direction = go to PREVIOUS */}
          <button
            aria-label="عکس قبلی"
            onClick={(e) => { e.stopPropagation(); prevMobile(e); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-full bg-black/40 text-white backdrop-blur-sm"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
          {/* RTL: left side = end of reading direction = go to NEXT */}
          <button
            aria-label="عکس بعدی"
            onClick={(e) => { e.stopPropagation(); nextMobile(e); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-full bg-black/40 text-white backdrop-blur-sm"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
        </>
      )}

      {/* Dot indicators (up to 5) */}
      {count > 1 && count <= 7 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
          {images.map((_, i) => (
            <span
              key={i}
              className={`block rounded-full transition-all duration-200 ${
                i === mobileIndex
                  ? "w-4 h-1.5 bg-white"
                  : "w-1.5 h-1.5 bg-white/50"
              }`}
            />
          ))}
        </div>
      )}

      {/* Counter fallback for many images — right-aligned (RTL start side) */}
      {count > 7 && (
        <div className="absolute bottom-2 right-3 bg-black/50 text-white text-xs font-myYekanFaNumRegular px-2.5 py-1 rounded-full">
          {mobileIndex + 1} / {count}
        </div>
      )}
    </div>
  );

  // ── 💻 Desktop: 1 image ─────────────────────────────────────────────────────
  if (count === 1) {
    return (
      <>
        <MobileGallery />
        <div className={GRID_WRAPPER}>
          <Thumb
            img={images[0]}
            index={0}
            restaurantName={restaurantName}
            baseUrl={baseUrl}
            className="flex-1 h-full"
            onClick={openModal}
          />
        </div>
        {isModalOpen && (
          <ImageModal
            images={images}
            currentIndex={selectedIndex}
            restaurantName={restaurantName}
            baseUrl={baseUrl}
            onClose={closeModal}
            onNext={next}
            onPrev={prev}
          />
        )}
      </>
    );
  }

  // ── 💻 Desktop: 2 images ────────────────────────────────────────────────────
  if (count === 2) {
    return (
      <>
        <MobileGallery />
        <div className={GRID_WRAPPER}>
          {images.map((img, i) => (
            <Thumb
              key={img.image_id}
              img={img}
              index={i}
              restaurantName={restaurantName}
              baseUrl={baseUrl}
              className="flex-1 h-full"
              onClick={openModal}
            />
          ))}
        </div>
        {isModalOpen && (
          <ImageModal
            images={images}
            currentIndex={selectedIndex}
            restaurantName={restaurantName}
            baseUrl={baseUrl}
            onClose={closeModal}
            onNext={next}
            onPrev={prev}
          />
        )}
      </>
    );
  }

  // ── 💻 Desktop: 3 images ────────────────────────────────────────────────────
  // RTL layout (flex-row-reverse): dominant RIGHT (first in DOM) + 2 stacked LEFT
  if (count === 3) {
    return (
      <>
        <MobileGallery />
        <div className={GRID_WRAPPER}>
          {/* Dominant — first in DOM → renders on the RIGHT (flex-row-reverse) */}
          <Thumb
            img={images[0]}
            index={0}
            restaurantName={restaurantName}
            baseUrl={baseUrl}
            className="flex-[2] h-full"
            onClick={openModal}
          />
          {/* Two stacked — second in DOM → renders on the LEFT */}
          <div className="flex flex-col gap-2 flex-1 h-full">
            {images.slice(1, 3).map((img, i) => (
              <Thumb
                key={img.image_id}
                img={img}
                index={i + 1}
                restaurantName={restaurantName}
                baseUrl={baseUrl}
                className="flex-1 w-full"
                onClick={openModal}
              />
            ))}
          </div>
        </div>
        {isModalOpen && (
          <ImageModal
            images={images}
            currentIndex={selectedIndex}
            restaurantName={restaurantName}
            baseUrl={baseUrl}
            onClose={closeModal}
            onNext={next}
            onPrev={prev}
          />
        )}
      </>
    );
  }

  // ── 💻 Desktop: 4+ images ──────────────────────────────────────────────────
  // RTL layout (flex-row-reverse):
  //   dominant RIGHT (flex-3, first in DOM)
  //   secondary column LEFT (flex-2, second in DOM):
  //     ┌─────────────────────┐
  //     │      img-2 top      │
  //     ├──────────┬──────────┤
  //     │ img-4+N  │  img-3   │  ← +N on far-left, img-3 adjacent to dominant
  //     └──────────┴──────────┘
  const displayed = images.slice(0, 4);
  const extra = count - 4;

  return (
    <>
      <MobileGallery />
      <div className={GRID_WRAPPER}>
        {/* Dominant — first in DOM → renders on the RIGHT (flex-row-reverse) */}
        <Thumb
          img={displayed[0]}
          index={0}
          restaurantName={restaurantName}
          baseUrl={baseUrl}
          className="flex-[3] h-full"
          onClick={openModal}
        />

        {/* Secondary column — second in DOM → renders on the LEFT */}
        <div className="flex flex-col gap-2 flex-[2] h-full min-w-0">
          {/* Top: full width of the left column */}
          <Thumb
            img={displayed[1]}
            index={1}
            restaurantName={restaurantName}
            baseUrl={baseUrl}
            className="flex-1 w-full"
            onClick={openModal}
          />
          {/* Bottom row — RTL flex: first child = rightmost (adjacent to dominant)
               img-3 RIGHT (adjacent to dominant) | img-4+N LEFT (far corner) */}
          <div className="flex gap-2 flex-1 min-h-0">
            <Thumb
              img={displayed[2]}
              index={2}
              restaurantName={restaurantName}
              baseUrl={baseUrl}
              className="flex-1 h-full"
              onClick={openModal}
            />
            <Thumb
              img={displayed[3]}
              index={3}
              restaurantName={restaurantName}
              baseUrl={baseUrl}
              extraCount={extra > 0 ? extra : undefined}
              className="flex-1 h-full"
              onClick={openModal}
            />
          </div>
        </div>
      </div>
      {isModalOpen && (
        <ImageModal
          images={images}
          currentIndex={selectedIndex}
          restaurantName={restaurantName}
          baseUrl={baseUrl}
          onClose={closeModal}
          onNext={next}
          onPrev={prev}
        />
      )}
    </>
  );
};

// ─── Modal ────────────────────────────────────────────────────────────────────
interface ImageModalProps {
  images: Image[];
  currentIndex: number;
  restaurantName: string;
  baseUrl?: string;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
}

const ImageModal: React.FC<ImageModalProps> = ({
  images,
  currentIndex,
  restaurantName,
  baseUrl,
  onClose,
  onNext,
  onPrev,
}) => {
  const current = images[currentIndex];
  const hasMany = images.length > 1;

  // Keyboard navigation — RTL-aware:
  // ArrowRight → previous (toward RTL start = right)
  // ArrowLeft  → next     (toward RTL end   = left)
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape")      onClose();
      if (e.key === "ArrowRight")  onPrev();
      if (e.key === "ArrowLeft")   onNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose, onNext, onPrev]);

  // Prevent body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onClick={onClose}
    >
      {/* Close */}
      <button
        aria-label="بستن"
        className="absolute top-4 right-4 z-10 flex items-center justify-center w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/25 transition-colors"
        onClick={onClose}
      >
        <XMarkIcon className="h-5 w-5" />
      </button>

      {/* Prev (RTL: right side of screen) */}
      {hasMany && (
        <button
          aria-label="عکس قبلی"
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-11 h-11 rounded-full bg-white/10 text-white hover:bg-white/25 transition-colors"
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
        >
          <ChevronRightIcon className="h-6 w-6" />
        </button>
      )}

      {/* Image */}
      <div
        className="flex flex-col items-center max-w-5xl w-full px-16 max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          key={current.image_id}
          src={getImageUrl(current.file_url, baseUrl)}
          alt={current.description || restaurantName}
          className="max-h-[80vh] max-w-full object-contain rounded-lg"
        />
        <div className="mt-3 text-white/70 text-sm font-myYekanFaNumRegular text-center select-none">
          {currentIndex + 1} / {images.length}
        </div>
        {current.description && (
          <p className="mt-1 text-white text-sm text-center font-myYekanFaNumRegular max-w-lg">
            {current.description}
          </p>
        )}
      </div>

      {/* Next (RTL: left side of screen) */}
      {hasMany && (
        <button
          aria-label="عکس بعدی"
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-11 h-11 rounded-full bg-white/10 text-white hover:bg-white/25 transition-colors"
          onClick={(e) => { e.stopPropagation(); onNext(); }}
        >
          <ChevronLeftIcon className="h-6 w-6" />
        </button>
      )}
    </div>
  );
};

export default ImageGallery;
